import React from 'react';
import { useForm } from 'react-hook-form';
import { Typography, Button } from 'antd';
import { useFela } from 'react-fela';
import { FelaStyle } from 'src/fela';
import { useTranslation } from 'react-i18next';
import colors from 'src/commons/colors';
import { FormInput } from 'src/commons/forms/components';
import { isCPF, formatToCPF } from 'brazilian-values';
import MyworkLogo from 'src/commons/components/Logo';
import MedicalCertificateIlustration from '../../../../../../../assets/images/documents/medical-certificates/medicalCertificateIlustration.svg';
import fireEvent from '../../../../../analytics/fireEvent';
import { cpfValidationUploadByEmployee } from '../../../analytics/events';

const { Text, Title } = Typography;

type CPFValidationProps = {
  onValidCpf: (cpf: string) => void;
};

export default function CPFValidation({ onValidCpf }: CPFValidationProps) {
  const { css } = useFela();
  const { t } = useTranslation();

  const CPF_PLACEHOLDER = '000.000.000-00';

  const methods = useForm();
  const {
    control,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  const validateCpf = () => {
    const cpf = getValues('cpf')?.replace(/\D/g, '');

    if (!cpf || cpf.length !== 11) {
      setError('cpf', {
        message: t(
          'medicalCertificates.uploadByEmployee.cpfValidation.formatError',
        ),
      });
      return false;
    }

    if (!isCPF(cpf)) {
      setError('cpf', {
        message: t(
          'medicalCertificates.uploadByEmployee.cpfValidation.invalidCpfError',
        ),
      });
      return false;
    }

    clearErrors('cpf');
    return cpf;
  };

  const onSubmit = async () => {
    const validCpf = validateCpf();
    if (validCpf) {
      fireEvent({
        ...cpfValidationUploadByEmployee,
      });
      onValidCpf(validCpf);
    }
  };

  const cpfHasError = errors?.cpf?.message;

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.logo)}>
        <MyworkLogo height={50} data-testid="mywork-logo" />
      </div>
      <Title level={5} className={css(styles.title)}>
        {t('medicalCertificates.uploadByEmployee.cpfValidation.title')}
      </Title>
      <div className={css(styles.logo)}>
        <MedicalCertificateIlustration data-testid="certificate-ilustration" />
      </div>
      <Text>
        {t('medicalCertificates.uploadByEmployee.cpfValidation.description')}
      </Text>
      <FormInput
        name="cpf"
        formatter={formatToCPF}
        control={control}
        autoFocus
        blurWithError
        onBlur={() => validateCpf()}
        maxLength={14}
        placeholder={CPF_PLACEHOLDER}
        className={css(styles.input)}
        showErrorMessage={false}
        data-testid="cpf-validation-input"
        inputMode="numeric"
        size="large"
      />
      {cpfHasError && (
        <Text type="danger" className={css(styles.errorText)}>
          {errors?.cpf?.message}
        </Text>
      )}
      <div>
        <Button
          className={css(styles.submitButton)}
          htmlType="submit"
          type="primary"
          onClick={onSubmit}
          data-testid="cpf-validation-button"
          size="large"
        >
          {t('medicalCertificates.uploadByEmployee.cpfValidation.button')}
        </Button>
      </div>
    </div>
  );
}

const styles: FelaStyle = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    textAlign: 'center',
  },
  logo: {
    alignSelf: 'center',
    paddingBottom: '1.5rem',
  },
  title: {
    fontSize: '1rem',
    lineHeight: '1.5rem',
    color: colors.text.lightGray,
    fontWeight: 500,
  },
  text: {
    fontSize: '0.875rem',
    lineHeight: '1.375rem',
    color: colors.text.darkGrey,
    fontWeight: 400,
  },
  errorText: {
    textAlign: 'left',
    fontSize: '0.875rem',
    lineHeight: '1.375rem',
    fontWeight: 400,
    marginBottom: 0,
  },
  submitButton: {
    marginTop: '1rem',

    width: '100%',
    '@media (min-width: 768px)': {
      width: '6.25rem',
    },
  },
};
